<template>
  <div class="sortable-highlighted-offers">
    <v-row class="pl-3">
      <span class="section-title pr-2">{{ `Ofertas ${type === 'ativaveis' ? 'ativáveis' : 'livres'} em destaque` }}</span>
      <p class="subtitle">A ordem definida aqui será aplicada para todas as lojas selecionadas</p>
    </v-row>
    <div class="headers-grid-container">
      <div
        class="table-headers content-grid"
        :class="{
          'content-grid-cashback-or-points-only': xorCashbackOrPoints,
          'content-grid-without-cashback-or-points-only': noCashbackOrPoints,
        }"
      >
        <div></div>
        <p class="column-title">Imagem</p>
        <p class="column-title">Código</p>
        <p class="column-title">Nome em exibição</p>
        <p class="column-title">Tipo</p>
        <p class="column-title">Formato</p>
        <p class="column-title">Validade</p>
        <p class="column-title">Desconto</p>
        <p v-if="isCashback" class="column-title">Cashback</p>
        <p v-if="isPoints" class="column-title">Pontos extras</p>
      </div>
    </div>
    <mf-loading loader="fetch-highlighted" loader-type="spinner">
      <v-card
        v-for="(item, index) of dynamicHighlightedOffers"
        :key="`offer-${index}`"
        class="my-3 drag-box"
        :class="{ over: toDropPosition === index }"
        :draggable="false"
        @drop="drop(index)"
        @dragenter="dragEnter(index)"
        @dragleave="dragLeave"
        @dragover="dragOver($event, index)"
      >
        <v-card-text class="py-3 px-3">
          <v-row class="px-3">
            <span class="px-2 mr-4 big-number index-highlighted">{{ index + 1 }}º</span>
            <v-card
              color="cinza-l90"
              flat
              draggable
              :class="{ dragged: draggedObject._id === item._id, eventable: !isDraggingObject || isManuallySorting, draggablecard: true }"
              @dragstart="dragStart(item, index)"
              @dragend="dragEnd"
            >
              <v-card-text
                class="pa-0 table-content content-grid"
                :class="{
                  'content-grid-cashback-or-points-only': xorCashbackOrPoints,
                  'content-grid-without-cashback-or-points-only': noCashbackOrPoints,
                }"
              >
                <mf-icon class="icon-color">drag_indicator</mf-icon>
                <div class="img-container">
                  <v-img v-if="item.image_url" :src="item.image_url" class="td-img" />
                  <mf-icon v-else class="td-img">image_not_supported</mf-icon>
                </div>
                <p>
                  {{ item.sku }}
                </p>
                <div>
                  <v-tooltip top max-width="210">
                    <template v-slot:activator="{ on }">
                      <div v-on="item.name.length > 23 ? on : null">
                        <p>{{ formatProductName(item.name) }}</p>
                      </div>
                    </template>

                    <span v-if="item.name.length > 23">{{ item.name }}</span>
                  </v-tooltip>
                </div>
                <p>
                  {{ getProductTypeLabel(item.personalized) }}
                </p>
                <p>
                  {{ getProductFormatTypeLabel(item.type) }}
                </p>
                <p>
                  {{ validityDates(item.start_date, item.end_date) }}
                </p>
                <div>
                  <p v-if="item.discount_percentage">{{ formatPercent(item.discount_percentage) }}</p>
                  <p v-else>-</p>
                </div>
                <div v-if="isCashback">
                  <p v-if="item.cashback_absolute">{{ item.cashback_absolute | localeString(2) | currency }}</p>
                  <p v-else-if="item.cashback_percentage">{{ item.cashback_percentage | localeString(0) | percent }}</p>
                  <p v-else>-</p>
                </div>
                <div v-if="isPoints">
                  <p v-if="item.additional_points_rate">{{ item.additional_points_rate | localeString(0) }}%</p>
                  <p v-else>-</p>
                </div>
                <div>
                  <mf-icon :class="{ eventable: !isDraggingObject }" class="icon-color cursor-pointer" @click="remove(index, item._id)">delete</mf-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-row>
        </v-card-text>
      </v-card>
    </mf-loading>
    <v-row class="py-4">
      <v-col class="text-center texto-corrido"> As demais posições serão preenchidas seguindo a ordem das ofertas em exibição no aplicativo </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapWaitingActions } from 'vue-wait'
import { cloneDeep } from 'lodash'
import { floatToPercentString } from '@/config/helpers.js'
import { getProductTypeLabel, getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { DateTime } from 'luxon'

export default {
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    toDropPosition: null,
    isManuallySorting: false,
  }),
  computed: {
    isCashback() {
      return this.$getAllowedModules()['cashback']
    },
    isPoints() {
      return this.$getAllowedModules()['points']
    },
    xorCashbackOrPoints() {
      return (this.isCashback || this.isPoints) && !(this.isPoints && this.isCashback)
    },
    noCashbackOrPoints() {
      return !this.isPoints && !this.isCashback
    },
    unitsToEdit() {
      return this.$store.getters.selectedUnits
    },
    draggedObject() {
      return this.$store.getters.draggedObject
    },
    isDraggingObject() {
      return this.$store.getters.isDraggingObject
    },
    dynamicHighlightedOffers() {
      const items = cloneDeep(this.$store.getters.highlightedOffers)
      return items
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
  },
  created() {
    this.fetchHighlightedOffers({
      app_units: this.unitsToEdit.map((unit) => unit.id_erp),
      activatable: this.type === 'ativaveis',
      franchise: this.isFranchisee,
    })

    document.addEventListener('drag', (event) => {
      if (event.clientY < 150 && document.documentElement.scrollTop !== 0) {
        window.scrollTo(0, document.documentElement.scrollTop - 15)
      }
    })
  },
  methods: {
    getProductTypeLabel,
    getProductFormatTypeLabel,
    ...mapWaitingActions({
      addHighlightedOffer: 'add-highlighted-offer',
      removeHighlightedOffer: 'remove-highlighted-offer',
      rearrangeHighlightedOffer: 'rearrange-highlighted-offer',
      setDraggedObject: 'set-dragged-object',
      unsetDraggedObject: 'unset-dragged-object',
      fetchHighlightedOffers: 'fetch-highlighted',
    }),
    formatProductName(name) {
      if (name.length <= 23) return name
      return `${name.substring(0, 23)}...`
    },
    formatPercent(value) {
      return floatToPercentString(value)
    },
    validityDates(startDate, endDate) {
      return `${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(endDate).toFormat('dd/MM/yyyy')}`
    },
    remove(position, offerId) {
      this.removeHighlightedOffer({
        position,
        offerId,
      })
    },
    drop(position) {
      if (!this.isManuallySorting) {
        // Is a new item
        this.addHighlightedOffer({ item: this.draggedObject, position })
      } else {
        this.rearrangeHighlightedOffer({ item: this.draggedObject, newPosition: position, oldPosition: this.draggedObject.originalPosition })
      }

      this.dragLeave()
    },
    dragStart(item, index) {
      this.isManuallySorting = true
      this.setDraggedObject({ ...item, originalPosition: index })
    },
    dragEnd() {
      this.isManuallySorting = false
      this.unsetDraggedObject()
    },
    dragEnter(position) {
      this.toDropPosition = position
    },
    dragLeave() {
      this.toDropPosition = null
    },
    dragOver(evt, position) {
      this.toDropPosition = position
      evt.preventDefault()
    },
  },
}
</script>

<style lang="scss">
.headers-grid-container {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0 10px;
}

.table-headers {
  width: calc(100% - 60px);
  & p {
    font-size: 14px;
    font-weight: bold;
    color: $cinza-d20;
    margin: 0;
  }
}

.content-grid {
  display: grid;
  align-items: center;
  grid-template-columns: 40px 60px 80px repeat(7, 1fr) 60px;
  gap: 15px;
}

.content-grid-cashback-or-points-only {
  grid-template-columns: 40px 60px 80px repeat(6, 1fr) 60px;
}

.content-grid-without-cashback-or-points-only {
  grid-template-columns: 40px 60px 80px repeat(5, 1fr) 60px;
}

.table-content {
  height: 66px;

  & p {
    padding-left: 5px;
    font-size: 14px;
    color: $cinza-d10;
    margin: 0;
  }
}

.sortable-highlighted-offers {
  td {
    @extend .texto-corrido;
  }

  .subtitle {
    width: 100%;
    font-size: 16px;
  }

  .itens-table {
    td {
      padding: 5px;
    }
  }
  .draggablecard {
    flex: 1;
    padding-right: 5px;
    cursor: move; // caso o navegador não suporte 'grab'
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
  }
  .drag-box {
    &.over {
      background-color: $marca-0-l90;
      transition: all 200ms;
    }
    .eventable {
      pointer-events: auto; // Necessario para permitir clicks enquanto não estiver draggeando
    }
  }

  .drag-text {
    color: $marca-0-l40;
    font-size: 12px;
  }

  .dragged {
    background-color: $cinza-l30 !important;
  }

  .td-img {
    width: 40px;
    height: 40px;
  }

  .index-highlighted {
    align-self: center;
    font-size: 26px;
    color: $cinza-d10;
  }
}

.icon-color {
  color: $cinza-l30 !important;
}
</style>
